// ─────────────────────────────────────────────────────────────────────────────
// MerchBestsellers, the Merch Manager secondary strip (movers slot, replacing
// Biggest movers). Top 5 best-selling products (units · revenue, 30d) from the
// dashboard merch summary, in the .mover card style. Port of the merch-spotlight
// best-sellers list (dashboard-wiring.js paintMerchSpotlight).
// ─────────────────────────────────────────────────────────────────────────────

import type { JSX, CSSProperties } from 'react';
import type { MerchSummary, MerchBestseller } from '../../lib';
import { merchMoney } from './MerchSpotlight';

// Real product image over a black backstop, or the gradient/rank fallback. The
// url is sanitized so a stray quote/paren can't break out of url().
function thumbStyle(p: MerchBestseller): CSSProperties | undefined {
  if (!p.image_url) return undefined;
  const safe = p.image_url.replace(/["'()\\\s]/g, (c) => encodeURIComponent(c));
  return {
    backgroundColor: '#000',
    backgroundImage: "url('" + safe + "')",
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };
}

export interface MerchBestsellersProps {
  readonly merch: MerchSummary | null;
}

export function MerchBestsellers({ merch }: MerchBestsellersProps): JSX.Element {
  const cur = merch?.currency || 'USD';
  const top = (merch?.bestsellers ?? []).slice(0, 5);
  const hasData = top.length > 0;

  return (
    <section aria-labelledby="merch-best-label">
      <div className="section-label">
        <h3 className="section-label__title" id="merch-best-label">Best sellers</h3>
        <span className="section-label__meta">UNITS · 30D</span>
      </div>
      <ul className="movers">
        {hasData
          ? top.map((p, i) => {
              const units = p.units === 1 ? '1 unit' : p.units + ' units';
              return (
                <li className="mover" key={String(p.product_id ?? i)}>
                  <div className="mover__thumb" style={thumbStyle(p)}>
                    {!p.image_url && <span className="mover__rank">#{i + 1}</span>}
                  </div>
                  <div className="mover__body">
                    <span className="mover__plat" title={p.title}>{p.title}</span>
                    <span className="mover__src">{p.variant || 'Merch'}</span>
                    <span className="mover__change neutral">{units} · {merchMoney(p.revenue, cur)}</span>
                  </div>
                </li>
              );
            })
          : [1, 2, 3, 4, 5].map((n) => (
              <li className="mover mover--empty" key={n}>
                <div className="mover__thumb">
                  <span className="mover__rank">#{n}</span>
                </div>
                <div className="mover__body">
                  <span className="mover__plat">-</span>
                  <span className="mover__change">No sales yet</span>
                </div>
              </li>
            ))}
      </ul>
    </section>
  );
}
